import React, { useState, Fragment, useEffect, useMemo } from 'react';
import _ from 'lodash';


import { CoverDataItem, PDFImageProps, User } from '../../../utils/models';
import { DEFAULT_REPORT_TITLE, FORBIDDEN_VALUES, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { getCurrentUser } from '../../../services/authService';
import { reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';

import Button from '../../themed/button';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import Conditional from '../../common/conditional';
import Drawing from '../../forms/custom-lpt/drawing';
import Elevation from '../../themed/instruction-modals/elevation';
import FVICoverData from '../../themed/fvi-cover-data';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import LPTReport from '.';
import LPTReportForm from '../../forms/custom-lpt/lptReportForm';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import VIPhotoRecordForm from '../../forms/visual-inspection/viPhotoRecordForm';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomLPTReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { role } = getCurrentUser() as User;

	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [ipiReport, setIPIReport] = useState(initialIPIData);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [certificates, setCertificates] = useState<ICertificate>();

	const reportDetails = useMemo(() => {
		return {
			projectDetails,
			ipiReport,
			coverData,
			executiveSummary,
			reportTitle,
			photoRecord,
			drawing,
			certificates,
		};
	}, [projectDetails, certificates, ipiReport, coverData, executiveSummary, reportTitle, photoRecord, drawing]);

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
			return 'Provide a valid work order number';
		}

		if (!reportDetails.ipiReport.aerosolsType) return 'Provide a valid Aerosols Type';

		if (!reportDetails.ipiReport.calibrationDueDate) return 'Provide a valid calibration due date';

		if (!reportDetails.ipiReport.penetrantExpiryDate) return 'Provide a valid penetrant expiry date';
		
		if (!reportDetails.ipiReport.penetrantRemoverExpiryDate) return 'Provide a valid penetrant remover expiry date';
		
		if (!reportDetails.ipiReport.developerExpiryDate) return 'Provide a valid developer expiry date';
		
		if (!reportDetails.ipiReport.penetrantBatchNumber) return 'Provide a valid penetrant batch number';

		if (!reportDetails.ipiReport.penetrantRemoverBatchNumber) return 'Provide a valid penetrant remover batch number';
		
		if (!reportDetails.ipiReport.developerBatchNumber) return 'Provide a valid developer batch number';
		
		if (!reportDetails.ipiReport.modal) return 'Provide a valid modal value';

		if (reportDetails.ipiReport.modal) {
			const modalValue = _.isString(reportDetails.ipiReport.modal) ? reportDetails.ipiReport.modal : (reportDetails.ipiReport.modal as CoverDataItem).value;
			if (FORBIDDEN_VALUES.includes(modalValue.toUpperCase()))
				return 'Provide a valid modal value';
		}

		if (!reportDetails.ipiReport.serialNumber) return 'Provide a valid serial number value';
		
		if (reportDetails.ipiReport.serialNumber && FORBIDDEN_VALUES.includes(reportDetails.ipiReport.serialNumber.toUpperCase())) return 'Provide a valid serial number value';

		if (reportDetails.executiveSummary.length === 0) return 'Enter a valid summary!';

		if (reportDetails.photoRecord.length === 0) return 'Upload at least one photo!';

		if (reportDetails.drawing.length === 0) return 'General drawing not uploaded!';

		return '';
	}, [reportDetails]);

	const mappedPhotoNumbers = useMemo(() => {
		return photoRecord.map((photo) => ({ label: photo.caption, value: photo.refNo }));
	}, [photoRecord]);

	const togglePreviewReport = (): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	};

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	useEffect(() => {
		function initiateCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return;
			}
			
			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}

			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setDrawing(cachedReport.drawing);
			setIPIReport(cachedReport.ipiReport);
			setReportTitle(cachedReport.reportTitle);

			const summary = cachedReport.ipiReport.summary || cachedReport.executiveSummary || '';
			setExecutiveSummary(summary);

			const orderedPhotos = reArrangePhotoRecord(cachedReport.photoRecord);
			dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
			setPhotoRecord(orderedPhotos);
		}

		initiateCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.LPT));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<LPTReport {...reportDetails} />
			</Modal>

			<FVICoverData
				coverDataId={config.coverDataTemplates.exxonMobilLPT}
				isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilLPT && role.approveReports}
				subtitle='Update the default data for LPT Cover Data. This is the default data used on all LPT Reports'
			/>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title='Report Incomplete' body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

			<CustomCertificateForm calibrationRequired={false} certificates={certificates} onChangeCertificate={setCertificates} />

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm  initialData={coverData} onAddCoverForm={setCoverData}  />
			</Zippy>

			<Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
				<Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<Zippy headerTitle="Liquid Penetrant Test Report" isValid={ipiReport.data.length > 0 && ipiReport.equipment.length > 0}>
				<LPTReportForm initialLPTData={ipiReport} onAdd={setIPIReport} photoNumbers={mappedPhotoNumbers} />
			</Zippy>

			<Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm initialGeneralPhotos={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving..." />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialIPIData = {
	developingTime: '10 min',
	dateOfInspection: new Date(),
	calibrationDueDate: new Date(),
	penetrantExpiryDate: new Date(),
	penetrantRemoverExpiryDate: new Date(),
	developerExpiryDate: new Date(),
	penetrantBatchNumber: '',
	penetrantRemoverBatchNumber: '',
	developerBatchNumber: '',
	modal: '',
	aerosolsType: '',
	serialNumber: '',
	surfaceCondition: 'As welded & Cleaned',
	surfaceTemperature: 'Ambinet & not more than 40deg',
	developer: 'Chemetall Ardros 9D1B',
	dwellTime: '10 min',
	penetrant: 'Chemetall Ardros 996 PB',
	penetrantRemover: 'Chemetall Ardros 9PR5',
	refStd: 'ASME Sec V, Article 6 Ed 2021 Section VIII',
	acceptanceStd: 'AWS D 1.1',
	material: 'Carbon steel',
	method: 'Visible Dye',
	stageOfTest: 'After Weld',
	equipment: 'Penetrant Testing Kit & Rags',
	light: 'Visible Light',
	technique: 'Non -Fluorescence technique',
	summary: '',
	data: [],
};

export default CustomLPTReport;
